// AdminDailyReportPage.jsx

import React, { useState, useEffect, useMemo } from 'react';
import API from '../services/api';

// Utility: format "YYYY-MM-DD" or "2025-03-15T..." into friendly date
function formatSessionDate(rawDate) {
  if (!rawDate) return '';
  const dateString = rawDate.includes('T') ? rawDate : rawDate + 'T12:00:00';
  return new Date(dateString).toLocaleDateString();
}

// Utility: fallback pastel if no color_code is set
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}
const pastelColors = ['#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF'];

// We'll inject the final color from therapistPaySplits when rendering
// so getTherapistRowStyle just checks if the name has a color assigned
function getTherapistRowStyle(therapistName = '', paySplitsMap) {
  if (!therapistName) {
    return { backgroundColor: '#f5f5f5' };
  }
  // Look up color code
  const payObj = paySplitsMap[therapistName];
  if (payObj && payObj.colorCode) {
    return { backgroundColor: payObj.colorCode };
  }
  // Else fallback to pastel hashing
  const h = Math.abs(hashString(therapistName));
  const color = pastelColors[h % pastelColors.length];
  return { backgroundColor: color };
}

// Highlight Payment Type
function getPaymentStyle(paymentType) {
  const lower = (paymentType || '').toLowerCase();
  let bg = 'lightgray';
  let fg = '#000';
  if (lower.includes('cash')) {
    bg = 'green';
    fg = '#fff';
  } else if (lower.includes('card')) {
    bg = 'blue';
    fg = '#fff';
  } else if (lower.includes('check')) {
    bg = 'yellow';
    fg = '#000';
  }
  return {
    backgroundColor: bg,
    color: fg,
    padding: '2px 6px',
    borderRadius: '4px',
    display: 'inline-block',
  };
}

// New utility function to display session status badges
function getSessionStatusBadge(noShow, cancelation) {
  if (noShow) {
    return {
      label: 'No Show',
      style: {
        backgroundColor: '#ff6b6b',
        color: 'white',
        padding: '2px 6px',
        borderRadius: '4px',
        display: 'inline-block',
        fontSize: '0.85rem',
        fontWeight: 'bold'
      }
    };
  } else if (cancelation) {
    return {
      label: 'Canceled',
      style: {
        backgroundColor: '#ff9e40',
        color: 'white',
        padding: '2px 6px',
        borderRadius: '4px',
        display: 'inline-block',
        fontSize: '0.85rem',
        fontWeight: 'bold'
      }
    };
  }
  return { label: '', style: {} };
}

function AdminDailyReportPage() {
  // 1) NEW (UNPAID) SESSIONS
  const [newSessions, setNewSessions] = useState([]);
  const [newSortOption, setNewSortOption] = useState('date');
  const [newEditingRows, setNewEditingRows] = useState({});

  // 2) PAST SESSIONS
  const [statusFilter, setStatusFilter] = useState('SUBMITTED');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pastSessions, setPastSessions] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Mark as paid checkboxes
  const [markPaidMap, setMarkPaidMap] = useState({});

  // Sorting
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  // Editing for past sessions
  const [editingRows, setEditingRows] = useState({});

  // Payment Summary popup
  const [payPopupData, setPayPopupData] = useState(null);

  // Pay splits for each therapist: map "FirstName LastName" => { therapistId, paySplit, colorCode }
  const [therapistPaySplits, setTherapistPaySplits] = useState({});

  /* =========================== FETCH NEW SESSIONS =========================== */
  const fetchNewSessions = async () => {
    try {
      const res = await API.get('/admin/daily-report/unpaid');
      const sorted = sortNewSessions(res.data, newSortOption);
      setNewSessions(sorted);
    } catch (err) {
      console.error('Error fetching new sessions:', err);
      alert('Error fetching new sessions');
    }
  };
  const sortNewSessions = (sessions, sortBy) => {
    const s = [...sessions];
    switch (sortBy) {
      case 'date':
        s.sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
        break;
      case 'therapist':
        s.sort((a, b) => {
          const at = (a.therapist_last_name || '') + (a.therapist_first_name || '');
          const bt = (b.therapist_last_name || '') + (b.therapist_first_name || '');
          return at.localeCompare(bt);
        });
        break;
      case 'client':
        s.sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
        break;
      default:
        break;
    }
    return s;
  };
  const handleNewSortChange = (e) => {
    const val = e.target.value;
    setNewSortOption(val);
    setNewSessions((prev) => sortNewSessions(prev, val));
  };

  // Mark a new session's status (PAID/DEBT/SUBMITTED)
  const handleStatusChange = (sessionId, newStatus) => {
    setNewSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, newStatus } : s))
    );
  };
  const handleSubmitChanges = async () => {
    try {
      const updates = newSessions
        .filter((s) => ['PAID', 'DEBT', 'SUBMITTED'].includes(s.newStatus))
        .map((s) => ({ sessionId: s.id, status: s.newStatus }));
      if (updates.length === 0) {
        alert('No changes to submit.');
        return;
      }

      await API.put('/admin/daily-report', { updates });
      // remove updated from newSessions
      setNewSessions((prev) =>
        prev.filter((s) => !['PAID', 'DEBT', 'SUBMITTED'].includes(s.newStatus))
      );

      fetchNewSessions();
      handleSearchPastSessions(true);

      alert('Daily report updated successfully!');
    } catch (err) {
      console.error('Error updating daily report:', err);
      alert('Error updating daily report');
    }
  };

  // Editing logic for new sessions
  const startEditingNewSession = (row) => {
    setNewEditingRows((prev) => ({
      ...prev,
      [row.id]: {
        ...row,
        session_date: new Date(row.session_date).toISOString().split('T')[0],
      },
    }));
  };
  const cancelEditingNewSession = (id) => {
    setNewEditingRows((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };
  const handleEditNewChange = (id, field, value) => {
    setNewEditingRows((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };
  const saveNewEdits = async (id) => {
    const rowToSave = newEditingRows[id];
    if (!rowToSave) return;
    try {
      await API.put('/admin/daily-report/update', {
        id: rowToSave.id,
        status: rowToSave.status,
        billed_amount: rowToSave.billed_amount,
        collected_amount: rowToSave.collected_amount,
        notes: rowToSave.notes,
        service_type: rowToSave.service_type,
        session_date: rowToSave.session_date,
        no_show: rowToSave.no_show || false,
        cancelation: rowToSave.cancelation || false
      });
      alert('Entry updated!');

      setNewSessions((prev) =>
        prev.map((ns) => (ns.id === id ? { ...ns, ...rowToSave } : ns))
      );
      cancelEditingNewSession(id);
    } catch (err) {
      console.error('Error saving new session edits:', err);
      alert('Error saving new session edits');
    }
  };
  const deleteNewSession = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await API.delete(`/admin/daily-report/${id}`);
      alert('Session deleted');
      setNewSessions((prev) => prev.filter((ns) => ns.id !== id));
      cancelEditingNewSession(id);
    } catch (err) {
      console.error('Error deleting session:', err);
      alert('Error deleting session');
    }
  };

  /* =========================== PAST SESSIONS =========================== */
  const handleSearchPastSessions = async (resetPage = true) => {
    try {
      let pageToUse = resetPage ? 0 : page;
      if (resetPage) setPage(0);

      let url = `/admin/daily-report/history?limit=21&offset=${pageToUse * 20}`;
      url += `&status=${statusFilter}`;
      if (startDate) url += `&start=${startDate}`;
      if (endDate) url += `&end=${endDate}`;
      if (searchTerm) url += `&searchTerm=${encodeURIComponent(searchTerm)}`;

      const res = await API.get(url);
      const data = res.data || [];
      const weHaveMore = data.length > 20;
      const first20 = data.slice(0, 20);

      if (resetPage) {
        setPastSessions(first20);
      } else {
        setPastSessions((prev) => [...prev, ...first20]);
      }
      setHasMore(weHaveMore);
    } catch (error) {
      console.error('Error fetching historical sessions:', error);
      alert('Error fetching historical sessions');
    }
  };
  const handleLoadOlder = () => setPage((p) => p + 1);

  // Sorting
  const getSortValue = (row, column) => {
    switch (column) {
      case 'therapist':
        return (row.therapist_last_name || '') + (row.therapist_first_name || '');
      case 'session_date':
        return row.session_date
          ? new Date(row.session_date + 'T12:00:00').getTime()
          : 0;
      case 'client':
        return row.client_name || '';
      case 'billed':
        return parseFloat(row.billed_amount) || 0;
      case 'collected':
        return parseFloat(row.collected_amount) || 0;
      case 'payment':
        return row.payment_type || '';
      case 'notes':
        return row.notes || '';
      case 'status':
        return row.status || '';
      case 'session_status':
        if (row.no_show) return 'No Show';
        if (row.cancelation) return 'Canceled';
        return '';
      default:
        return '';
    }
  };
  const handleColumnSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  const sortedPastSessions = useMemo(() => {
    if (!sortColumn) return pastSessions;
    const sorted = [...pastSessions].sort((a, b) => {
      const av = getSortValue(a, sortColumn);
      const bv = getSortValue(b, sortColumn);
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
    return sortDirection === 'asc' ? sorted : sorted.reverse();
  }, [pastSessions, sortColumn, sortDirection]);

  // Editing for Past sessions
  const startEditing = (ps) => {
    setEditingRows((prev) => ({
      ...prev,
      [ps.id]: {
        ...ps,
        session_date: new Date(ps.session_date).toISOString().split('T')[0],
      },
    }));
  };
  const cancelEditing = (id) => {
    setEditingRows((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };
  const handleEditChange = (id, field, value) => {
    setEditingRows((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };
  const saveEdits = async (id) => {
    const rowToSave = editingRows[id];
    if (!rowToSave) return;
    try {
      await API.put('/admin/daily-report/update', {
        id: rowToSave.id,
        status: rowToSave.status,
        billed_amount: rowToSave.billed_amount,
        collected_amount: rowToSave.collected_amount,
        notes: rowToSave.notes,
        service_type: rowToSave.service_type,
        session_date: rowToSave.session_date,
        no_show: rowToSave.no_show || false,
        cancelation: rowToSave.cancelation || false
      });
      alert('Entry updated!');
      setPastSessions((prev) =>
        prev.map((ps) => (ps.id === id ? { ...ps, ...rowToSave } : ps))
      );
      cancelEditing(id);
    } catch (err) {
      console.error('Error saving edits:', err);
      alert('Error saving edits.');
    }
  };
  const deletePastSession = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await API.delete(`/admin/daily-report/${id}`);
      alert('Session deleted');
      setPastSessions((prev) => prev.filter((ps) => ps.id !== id));
      cancelEditing(id);
    } catch (err) {
      console.error('Error deleting session:', err);
      alert('Could not delete session.');
    }
  };

  // Mark selected as Paid => show popup & post admin-only message
  const handleToggleMarkPaid = (id) => {
    setMarkPaidMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleMarkSelectedAsPaid = async () => {
    const selectedIds = Object.keys(markPaidMap).filter((sid) => markPaidMap[sid]);
    if (selectedIds.length === 0) {
      alert('No sessions selected to mark as paid.');
      return;
    }
    const selected = sortedPastSessions.filter((ps) =>
      selectedIds.includes(ps.id.toString())
    );

    // Sum by therapist
    const sumsByTherapist = {};
    for (let s of selected) {
      const nameKey = `${s.therapist_first_name} ${s.therapist_last_name}`.trim();
      if (!sumsByTherapist[nameKey]) sumsByTherapist[nameKey] = 0;
      sumsByTherapist[nameKey] += parseFloat(s.billed_amount) || 0;
    }

    // Update DB => status=PAID
    const updates = selectedIds.map((sid) => ({ sessionId: +sid, status: 'PAID' }));
    try {
      await API.put('/admin/daily-report', { updates });
    } catch (err) {
      console.error('Error marking sessions as paid:', err);
      alert('Error marking sessions as paid');
      return;
    }

    // Build array for the popup
    const breakdown = [];
    for (let nameKey of Object.keys(sumsByTherapist)) {
      const totalCollected = sumsByTherapist[nameKey];
      const payObj = therapistPaySplits[nameKey] || { therapistId: null, paySplit: '50', colorCode: '' };
      const parsedSplit = parseFloat(payObj.paySplit) || 50;
      const therapistPay = totalCollected * (parsedSplit / 100);
      const practicePay = totalCollected - therapistPay; // NEW

      breakdown.push({
        name: nameKey,
        totalCollected,
        paySplit: parsedSplit,
        therapistPay,
        practicePay
      });
    }

    // Show local popup
    setPayPopupData(breakdown);

    // Create single admin-only message
    const lines = breakdown.map((item) => {
      const tc = item.totalCollected.toFixed(2);
      const tp = item.therapistPay.toFixed(2);
      const pp = item.practicePay.toFixed(2);
      const ps = item.paySplit.toFixed(2);
      return `Therapist: ${item.name}, Total Collected: $${tc}, Split: ${ps}%, Therapist Pay: $${tp}, Practice: $${pp}`;
    });
    const messageBody = lines.join('<br/>');

    const msgTitle = 'Payment Summary (Admin Reference)';
    const msgContent = `The following payments were marked as paid:<br/>${messageBody}`;

    try {
      await API.post('/admin/messages', {
        title: msgTitle,
        content: msgContent,
        imageUrl: null,
        targetTherapistId: null,
        adminOnly: true
      });
    } catch (err) {
      console.error('Error creating Payment Summary message for admin:', err);
    }

    // Refresh
    handleSearchPastSessions(true);
    setMarkPaidMap({});
  };

  // Grab paySplit & colorCode from each therapist
  const fetchAllTherapistsPaySplit = async () => {
    try {
      const res = await API.get('/admin/all-therapists');
      const all = res.data || [];
      const payMap = {};
      for (let t of all) {
        const nameKey = `${t.first_name} ${t.last_name}`.trim();
        let paySplitVal = '50';
        let colorVal = '#FFFFFF';
        try {
          const gRes = await API.get(`/admin/therapist-goals/${t.id}`);
          paySplitVal = gRes.data.pay_split_percent || '50';
          colorVal = gRes.data.color_code || '#FFFFFF';
        } catch (err) {
          console.warn('No goals found for therapist', t.id);
        }
        payMap[nameKey] = {
          therapistId: t.id,
          paySplit: paySplitVal,
          colorCode: colorVal
        };
      }
      setTherapistPaySplits(payMap);
    } catch (err) {
      console.error('Error loading therapist pay splits/colors:', err);
    }
  };

  // useEffects
  useEffect(() => {
    fetchNewSessions();
    handleSearchPastSessions(true);
    fetchAllTherapistsPaySplit();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (page > 0) {
      handleSearchPastSessions(false);
    }
    // eslint-disable-next-line
  }, [page]);

  return (
    <div className="admin-daily-report-container">
      <h2>Admin Daily Report</h2>

      {/* PART 1: New Sessions */}
      <section>
        <h3>New Sessions</h3>
        <div className="daily-report-controls">
          <label>Sort New Entries By:</label>
          <select value={newSortOption} onChange={handleNewSortChange}>
            <option value="date">Date</option>
            <option value="therapist">Therapist</option>
            <option value="client">Client</option>
          </select>
        </div>

        {newSessions.length === 0 ? (
          <p>No new sessions found.</p>
        ) : (
          <>
            <table className="daily-report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Therapist</th>
                  <th>Client</th>
                  <th>Billed</th>
                  <th>Collected</th>
                  <th>Payment</th>
                  <th>Notes</th>
                  <th>Session Status</th>
                  <th>Mark as</th>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {newSessions.map((s) => {
                  const isEditing = !!newEditingRows[s.id];
                  const editRow = newEditingRows[s.id] || {};
                  const therapistName = `${s.therapist_first_name || ''} ${s.therapist_last_name || ''}`.trim();
                  const sessionStatus = getSessionStatusBadge(s.no_show, s.cancelation);

                  return (
                    <tr key={s.id} style={getTherapistRowStyle(therapistName, therapistPaySplits)}>
                      <td>
                        {isEditing ? (
                          <input
                            type="date"
                            value={editRow.session_date}
                            onChange={(e) =>
                              handleEditNewChange(s.id, 'session_date', e.target.value)
                            }
                          />
                        ) : (
                          formatSessionDate(s.session_date)
                        )}
                      </td>
                      <td>
                        {s.therapist_first_name} {s.therapist_last_name}
                      </td>
                      <td>{s.client_name}</td>
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            style={{ width: '80px' }}
                            value={editRow.billed_amount}
                            onChange={(e) =>
                              handleEditNewChange(s.id, 'billed_amount', e.target.value)
                            }
                          />
                        ) : (
                          `$${s.billed_amount}`
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            style={{ width: '80px' }}
                            value={editRow.collected_amount || 0}
                            onChange={(e) =>
                              handleEditNewChange(s.id, 'collected_amount', e.target.value)
                            }
                          />
                        ) : (
                          `$${s.collected_amount || 0}`
                        )}
                      </td>
                      <td>
                        <span style={getPaymentStyle(s.payment_type)}>
                          {s.payment_type || ''}
                        </span>
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            style={{ width: '100px' }}
                            value={editRow.notes || ''}
                            onChange={(e) => handleEditNewChange(s.id, 'notes', e.target.value)}
                          />
                        ) : (
                          s.notes || ''
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>
                              <input
                                type="checkbox"
                                checked={!!editRow.no_show}
                                onChange={(e) => handleEditNewChange(s.id, 'no_show', e.target.checked)}
                              /> No Show
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={!!editRow.cancelation}
                                onChange={(e) => handleEditNewChange(s.id, 'cancelation', e.target.checked)}
                              /> Canceled
                            </label>
                          </div>
                        ) : (
                          sessionStatus.label && <span style={sessionStatus.style}>{sessionStatus.label}</span>
                        )}
                      </td>
                      <td>
                        <div className="mark-as-container">
                          <label>
                            <input
                              type="radio"
                              name={`status-${s.id}`}
                              onChange={() => handleStatusChange(s.id, null)}
                              checked={!s.newStatus}
                            />
                            No Change
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`status-${s.id}`}
                              onChange={() => handleStatusChange(s.id, 'PAID')}
                              checked={s.newStatus === 'PAID'}
                            />
                            Paid
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`status-${s.id}`}
                              onChange={() => handleStatusChange(s.id, 'DEBT')}
                              checked={s.newStatus === 'DEBT'}
                            />
                            Debt
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`status-${s.id}`}
                              onChange={() => handleStatusChange(s.id, 'SUBMITTED')}
                              checked={s.newStatus === 'SUBMITTED'}
                            />
                            Submitted
                          </label>
                        </div>
                      </td>
                      <td>
                        {isEditing ? (
                          <>
                            <button onClick={() => saveNewEdits(s.id)}>Save</button>
                            <button onClick={() => deleteNewSession(s.id)}>Delete</button>
                            <button onClick={() => cancelEditingNewSession(s.id)}>Cancel</button>
                          </>
                        ) : (
                          <button onClick={() => startEditingNewSession(s)}>
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <button
              className="daily-report-submit-btn"
              onClick={handleSubmitChanges}
              style={{ marginTop: '0.5rem' }}
            >
              Submit Changes
            </button>
          </>
        )}
      </section>

      <hr />

      {/* PART 2: Past Sessions */}
      <section>
        <h3>Past Sessions</h3>
        <div className="filters-container">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ALL">All</option>
              <option value="UNPAID">Unpaid</option>
              <option value="PAID">Paid</option>
              <option value="DEBT">Debt</option>
              <option value="SUBMITTED">Submitted</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Client or Therapist"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <button onClick={() => handleSearchPastSessions(true)}>Search</button>
          </div>
        </div>

        {sortedPastSessions.length === 0 ? (
          <p>No past sessions found.</p>
        ) : (
          <>
            <table className="daily-report-table">
              <thead>
                <tr>
                  <th onClick={() => handleColumnSort('session_date')}>Date</th>
                  <th onClick={() => handleColumnSort('therapist')}>Therapist</th>
                  <th onClick={() => handleColumnSort('client')}>Client</th>
                  <th onClick={() => handleColumnSort('billed')}>Billed</th>
                  <th onClick={() => handleColumnSort('collected')}>Collected</th>
                  <th onClick={() => handleColumnSort('payment')}>Payment</th>
                  <th onClick={() => handleColumnSort('notes')}>Notes</th>
                  <th onClick={() => handleColumnSort('session_status')}>Session Status</th>
                  <th onClick={() => handleColumnSort('status')}>Status</th>
                  <th>Edit</th>
                  <th>Mark Paid</th>
                </tr>
              </thead>
              <tbody>
                {sortedPastSessions.map((ps) => {
                  const isEditing = !!editingRows[ps.id];
                  const editRow = editingRows[ps.id];
                  const therapistName = `${ps.therapist_first_name} ${ps.therapist_last_name}`.trim();
                  const sessionStatus = getSessionStatusBadge(ps.no_show, ps.cancelation);

                  return (
                    <tr key={ps.id} style={getTherapistRowStyle(therapistName, therapistPaySplits)}>
                      <td>
                        {isEditing ? (
                          <input
                            type="date"
                            value={editRow.session_date}
                            onChange={(e) =>
                              handleEditChange(ps.id, 'session_date', e.target.value)
                            }
                          />
                        ) : (
                          formatSessionDate(ps.session_date)
                        )}
                      </td>
                      <td>
                        {ps.therapist_first_name} {ps.therapist_last_name}
                      </td>
                      <td>{ps.client_name}</td>
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            value={editRow.billed_amount}
                            onChange={(e) =>
                              handleEditChange(ps.id, 'billed_amount', e.target.value)
                            }
                            style={{ width: '80px' }}
                          />
                        ) : (
                          `$${ps.billed_amount}`
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            value={editRow.collected_amount}
                            onChange={(e) =>
                              handleEditChange(ps.id, 'collected_amount', e.target.value)
                            }
                            style={{ width: '80px' }}
                          />
                        ) : (
                          `$${ps.collected_amount}`
                        )}
                      </td>
                      <td>
                        <span style={getPaymentStyle(ps.payment_type)}>
                          {ps.payment_type || ''}
                        </span>
                      </td>
                      <td>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editRow.notes || ''}
                            onChange={(e) => handleEditChange(ps.id, 'notes', e.target.value)}
                            style={{ width: '100px' }}
                          />
                        ) : (
                          ps.notes || ''
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label>
                              <input
                                type="checkbox"
                                checked={!!editRow.no_show}
                                onChange={(e) => handleEditChange(ps.id, 'no_show', e.target.checked)}
                              /> No Show
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={!!editRow.cancelation}
                                onChange={(e) => handleEditChange(ps.id, 'cancelation', e.target.checked)}
                              /> Canceled
                            </label>
                          </div>
                        ) : (
                          sessionStatus.label && <span style={sessionStatus.style}>{sessionStatus.label}</span>
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            value={editRow.status || ''}
                            onChange={(e) =>
                              handleEditChange(ps.id, 'status', e.target.value)
                            }
                          >
                            <option value="UNPAID">Unpaid</option>
                            <option value="PAID">Paid</option>
                            <option value="DEBT">Debt</option>
                            <option value="SUBMITTED">Submitted</option>
                          </select>
                        ) : (
                          ps.status
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <>
                            <button
                              style={{ marginRight: '0.5rem' }}
                              onClick={() => saveEdits(ps.id)}
                            >
                              Save
                            </button>
                            <button
                              style={{ marginRight: '0.5rem' }}
                              onClick={() => deletePastSession(ps.id)}
                            >
                              Delete
                            </button>
                            <button onClick={() => cancelEditing(ps.id)}>Cancel</button>
                          </>
                        ) : (
                          <button onClick={() => startEditing(ps)}>Edit</button>
                        )}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!markPaidMap[ps.id]}
                          onChange={() => handleToggleMarkPaid(ps.id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {hasMore && (
              <button onClick={handleLoadOlder} style={{ marginTop: '1rem' }}>
                Load Older Entries
              </button>
            )}

            <div style={{ marginTop: '1rem' }}>
              <button onClick={handleMarkSelectedAsPaid}>Mark Selected as Paid</button>
            </div>
          </>
        )}
      </section>

      {/* Payment Summary popup */}
      {payPopupData && (
        <div style={popupStyle}>
          <h3>Payment Summary</h3>
          <table border="1" cellPadding="4" style={{ marginBottom: '1rem' }}>
            <thead>
              <tr>
                <th>Therapist</th>
                <th>Total Collected</th>
                <th>Pay Split %</th>
                <th>Therapist Pay</th>
                <th>Practice</th>
              </tr>
            </thead>
            <tbody>
              {payPopupData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.name}</td>
                  <td>${row.totalCollected.toFixed(2)}</td>
                  <td>{row.paySplit.toFixed(2)}%</td>
                  <td>${row.therapistPay.toFixed(2)}</td>
                  <td>${row.practicePay.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={() => setPayPopupData(null)}>Close</button>
        </div>
      )}
    </div>
  );
}

const popupStyle = {
  position: 'fixed',
  top: '20%',
  left: '30%',
  right: '30%',
  background: '#fff',
  border: '2px solid #ccc',
  padding: '1rem',
  zIndex: 999
};

export default AdminDailyReportPage;